import { useCallback, useState } from 'react';
import { Flex, Button, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { Anthem } from '@/model';
import { useAllClub } from '@/services/queries/club';
import { AnthemTable } from '../AnthemTable/AnthemTable';
import { AnthemFilterBox } from '../AnthemFilterBox';
import { useDeleteAnthemMutation, useGetAnthem, useGetAnthemById } from '@/services/queries/anthem';
import { AnthemFilters } from '../../types';
import { AnthemCreateModal } from '../AnthemCreateModal';
import { AnthemEditModal } from '../AnthemEditModal';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { GetParamsObject } from '@/model/query-params';

export const AnthemMultiTeamView = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Anthem | undefined>(undefined);

  const { data: clubs = [] } = useAllClub(true);
  const deleteMutation = useDeleteAnthemMutation();
  const getItemByUUID = useGetAnthemById();

  const filtersForm = useFiltersForm<AnthemFilters>({ defaultValues: { team_uuid: '' } });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({ pageNumber: 1, pageSize: 10 });

  const params = GetParamsObject(filterData, pagination.pageNumber, pagination.pageSize, sorting.desc ? 'desc' : 'asc', sorting.field);
  const { data: itemData, isLoading, forceRefetch } = useGetAnthem(params);
  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: Anthem) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;
    setIsConfirmModalOpen(false);
    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({ title: t('pages.application_history_anthem.application_history_anthem_delete_success'), status: 'success' });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({ status: 'error', title: (resp && resp.message) || t('pages.application_history_anthem.application_history_anthem_delete_error') });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({ status: 'error', title: (resp && resp.message) || t('pages.application_history_anthem.application_history_anthem_edit_error') });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch]);

  return (
    <>
      <Flex justifyContent='flex-end'>
        <Button
          display='flex'
          alignItems='center'
          gap={2}
          onClick={() => setIsCreateModalOpen(true)}
          width={{ base: '100%', md: 'initial' }}
        >
          <ChakraFeatherIcon name='plus-circle' />
          {t('pages.application_history_anthem.application_history_anthem_add_button')}
        </Button>
      </Flex>

      <SectionBox bodyProps={{ gap: 'small' }}>
        <AnthemFilterBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
      </SectionBox>

      <Flex direction='column' gap='deka' mt={8}>
        <AnthemTable
          data={itemsData}
          isLoading={isLoading}
          sorting={sorting}
          pageNumber={pagination.pageNumber}
          pageSize={pagination.pageSize}
          totalPages={itemData?.meta.pages || 0}
          totalItems={itemData?.meta.total || 0}
          onSortingChange={(field, desc) => setSorting({ field, desc })}
          onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
          onEditClick={item => onHandleEdit(item.uuid)}
          onDeleteClick={item => onHandleDelete(item)}
        />
      </Flex>

      {selectedItem && (
        <AnthemEditModal
          item={selectedItem}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          onDone={onHandleEditDone}
        />
      )}

      <AnthemCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_history_anthem.application_history_anthem_delete_confirm_title')}
        description={
          <Text>
            {t('pages.application_history_anthem.application_history_anthem_delete_confirm_description_start')}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.lyrics}"
            </Text>
            {t('pages.application_history_anthem.application_history_anthem_delete_confirm_description_end')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || deleteMutation.isLoading} />
    </>
  );
};
